<script setup lang="ts">
import { useDragAndDrop } from "@/ui/composables/useDragAndDrop";
import { computed, watch } from "vue";
import { between, required } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import PriceModelCurveOffsetDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveOffsetDO;
import PriceAdjustmentType = Modules.BookingExperts.Enums.PriceAdjustmentType;

interface Props {
	offsets: PriceModelCurveOffsetDO[];
	priceAdjustmentType: PriceAdjustmentType;
	isPositive: boolean;
	isReadonly: boolean;
}

const props = defineProps<Props>();

const priceAdjustmentType = computed(() => props.priceAdjustmentType);

const emit = defineEmits<{
	"update:offsets": [offsets: PriceModelCurveOffsetDO[]];
	hover: [index: number, enter: boolean];
}>();

/**
 * We categorize offsets into "positive" or "negative" based on isPositive.
 * This splitting is purely for rendering, but we do not want to flip signs
 * as the user edits the numeric value. We'll preserve the original sign (or
 * the `isPositive` category) even if the user types "0".
 */
const filteredOffsets = computed(() =>
	props.offsets.filter((offset) =>
		props.isPositive
			? offset.offsetPercentage >= 0
			: offset.offsetPercentage < 0
	)
);

const originalIndices = computed(() =>
	props.offsets.reduce<number[]>((indices, offset, index) => {
		const matchesPositivity = props.isPositive
			? offset.offsetPercentage >= 0
			: offset.offsetPercentage < 0;
		if (matchesPositivity) indices.push(index);
		return indices;
	}, [])
);

/**
 * We display the absolute value so the text field is not prefixed with
 * minus sign for negative offsets. The prefix visually indicates sign.
 */
const priceField = computed(() =>
	priceAdjustmentType.value === "absolute"
		? "priceAbsoluteChange"
		: "pricePercentageChange"
);

const displayValues = computed(() =>
	filteredOffsets.value.map((offset) => ({
		offsetPercentage: Math.abs(offset.offsetPercentage ?? 0),
		priceChange: Math.abs((offset[priceField.value] as number) ?? 0),
	}))
);

const cumulativeFactors = computed(() => {
	let total = 0;
	return filteredOffsets.value.map((offset) => {
		total += (offset[priceField.value] as number) ?? 0;
		return total.toFixed(1);
	});
});

/**
 * Parse the provided string/number into a JS number, permitting null if the field
 * is cleared (so we can track an "empty" state).
 */
function parseAsNumber(value: string | number | null): number | null {
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed) {
			// user cleared the field
			return null;
		}
		return Number(trimmed) || 0;
	}
	return value ?? 0;
}

function updateOffset(
	value: string | number | null,
	index: number,
	field: "offsetPercentage" | "pricePercentageChange" | "priceAbsoluteChange"
) {
	if (value === "" || value === 0 || value === "0") {
		return;
	}

	const actualIndex = originalIndices.value[index];
	if (actualIndex === undefined) return;

	const newOffsets = [...props.offsets];
	const parsed = parseAsNumber(value);
	if (parsed === null) {
		newOffsets[actualIndex][field] = null as unknown as number;
	} else {
		const finalSign = props.isPositive ? 1 : -1;
		newOffsets[actualIndex][field] = parsed * finalSign;
	}

	emit("update:offsets", newOffsets);
}

/**
 * Drag and drop reordering logic.
 */
function handleDrop(ev: DragEvent, newIndex: number) {
	const oldIndex = parseInt(ev.dataTransfer?.getData("text/plain") ?? "0", 10);
	const [actualOldIndex, actualNewIndex] = [
		originalIndices.value[oldIndex],
		originalIndices.value[newIndex],
	];

	if (actualOldIndex === undefined || actualNewIndex === undefined) return;

	const newOffsets = [...props.offsets];
	const [movedItem] = newOffsets.splice(actualOldIndex, 1);
	newOffsets.splice(actualNewIndex, 0, movedItem);
	emit("update:offsets", newOffsets);
}

/**
 * Permit the user to delete offsets from this side.
 */
function deleteOffset(index: number) {
	const actualIndex = originalIndices.value[index];
	if (actualIndex === undefined) return;

	const newOffsets = props.offsets.filter((_, i) => i !== actualIndex);
	emit("update:offsets", newOffsets);
}

/** Setup the drag event handlers. */
const { dragstartHandle, dragoverHandle, dragleaveHandle, dragdropHandle } =
	useDragAndDrop(filteredOffsets.value);
</script>

<template>
	<div class="d-flex flex-column">
		<div
			v-for="(item, index) in filteredOffsets"
			:key="originalIndices[index]"
			class="offset-node"
			:draggable="!isReadonly"
			@mouseenter="() => emit('hover', index, true)"
			@mouseleave="() => emit('hover', index, false)"
			@dragstart="(ev) => dragstartHandle(ev, index)"
		>
			<i class="drag-handle fa-regular fa-grip-dots-vertical" />

			<div class="item-content">
				<v-row dense>
					<v-col cols="4">
						<v-text-field
							:model-value="displayValues[index].offsetPercentage"
							density="compact"
							variant="solo"
							label="Offset"
							append-inner-icon="fa-solid fa-percent"
							hide-details
							type="number"
							:prefix="isPositive ? '+' : '-'"
							:rules="[required(t), between(t, 0, 100)]"
							step="0.1"
							:disabled="isReadonly"
							hide-spin-buttons
							@update:model-value="
								(v) => updateOffset(v, index, 'offsetPercentage')
							"
						/>
					</v-col>
					<v-col cols="4">
						<v-text-field
							:key="priceAdjustmentType"
							:model-value="displayValues[index].priceChange"
							density="compact"
							variant="solo"
							label="Price Adjustment"
							:prefix="isPositive ? '+' : '-'"
							:append-inner-icon="
								priceAdjustmentType === 'relative'
									? 'fa-solid fa-percent'
									: 'fa-solid fa-euro-sign'
							"
							hide-details
							type="number"
							:rules="[
								required(t),
								priceAdjustmentType == 'relative'
									? between(t, 0, 100)
									: between(t, 0, 10000),
							]"
							:step="priceAdjustmentType == 'relative' ? 0.1 : 1.0"
							:disabled="isReadonly"
							hide-spin-buttons
							@update:model-value="(v) => updateOffset(v, index, priceField)"
						/>
					</v-col>
					<v-col
						cols="4"
						class="d-flex flex-row justify-space-between align-center"
						:key="priceAdjustmentType"
					>
						<div class="cumulative-factor">
							Total: {{ cumulativeFactors[index] }}
							<v-icon
								:icon="
									priceAdjustmentType === 'relative'
										? 'fa fa-percent'
										: 'fa fa-euro-sign'
								"
								size="18"
							/>
						</div>
						<button
							v-if="!isReadonly"
							class="delete-button"
							@click="deleteOffset(index)"
						>
							<i class="fa-solid fa-xmark" />
						</button>
					</v-col>
				</v-row>
			</div>

			<span
				class="order-dropzone"
				@dragover="(ev) => dragoverHandle(ev, index)"
				@dragleave="(ev) => dragleaveHandle(ev)"
				@drop="
					(ev) => {
						dragdropHandle(ev, index);
						handleDrop(ev, index);
					}
				"
			/>
		</div>
	</div>
</template>

<style scoped lang="scss">
.offset-node {
	position: relative;
	display: flex;
	align-items: center;
	height: 3.5rem;
	padding: 0.5rem;
	background: rgba(var(--color-background), 0.4);
	border: 1px solid rgb(var(--color-secondary));
	user-select: none;
	cursor: grab;
	transition: all 125ms ease;

	&:not(:first-child) {
		border-top: none;
	}

	&:first-child {
		border-radius: 8px 8px 0 0;
	}

	&:last-child {
		border-radius: 0 0 8px 8px;
	}

	&:hover {
		background-color: rgb(var(--color-background));
		border-color: #ccc;
	}
}

.item-content {
	flex-grow: 1;
	font-size: 1rem;
	color: #333;
	font-weight: 500;
	display: flex;
	flex-direction: column;
	line-height: normal;
	overflow: hidden;
}

.cumulative-factor {
	padding: 0.25rem;
	color: #666;
	font-size: 0.9rem;
	font-weight: 500;
}

.drag-handle {
	display: flex;
	align-items: center;
	justify-content: center;
	padding-right: 0.75rem;
	cursor: grab;
	color: #888;
	height: 1.25em;

	&:hover {
		color: #555;
	}
}

.delete-button {
	cursor: pointer;
	margin: 0 5px;
	padding: 0;
	color: #888;

	&:hover {
		color: #555;
	}
}

.order-dropzone {
	position: absolute;
	bottom: -0.5rem;
	left: 0;
	width: 100%;
	height: 1rem;
	z-index: 100;
	border: 1px dashed transparent;
	transition: all 125ms ease;

	&.over {
		border-color: #ff9800;
		background: rgba(255, 152, 0, 0.1);
	}
}

:deep(.v-field__append-inner) {
	font-size: 0.7em;
}

:deep(.v-field--variant-solo) {
	box-shadow: unset;
	border: 1px solid #e0e0e0;
}
</style>
